import { useAddress } from "@shared/context/AddressContext";
import Button from "@shared/components/Atoms/Button/Button";
import { IconNames } from "@shared/enums/icons.enums";
import { useTranslations } from "@shared/context/useTranslations";
import { cn } from "@shared/helpers/classNames.helper";
import EmptyState from "@shared/components/Atoms/EmptyState/EmptyState.component";
import { SvgIcon } from "@shared/components/Atoms/SvgIcon/SvgIcon";
import { useEffect, useRef, useState } from "react";
import { ModalButtonType, ModalSize, useModal } from "@shared/context/ModalContext";
import { useToast } from "@shared/context/ToastContext";
import { AddressEditRef, AddressEdit } from "@shared/components/Molecules/AddressEdit/AddressEdit.component";
import { UserAddress } from "@shared/types/UserAccountTypes.interface";
import { countryFromCode } from "@shared/helpers/countryHelper";
import S from "./AddressList.module.scss";

const AddressList = () => {
    const { addresses, blank, updateAddresses } = useAddress();
    const { showModal, closeModal } = useModal();
    const { showToast } = useToast();
    const { getTranslation } = useTranslations();
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [selectedAddress, setSelectedAddress] = useState<UserAddress | null>(null);
    const ref = useRef<AddressEditRef>(null);
    const inputAddressRef = useRef<UserAddress>();
    
    // Get the URL parameter
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const addressId = params.get("addressId");

        if (addresses && addresses.length > 0) {
            if (addressId && addresses.some((address) => address.id === addressId)) {
                setSelectedId(addressId);
            } else {
                setSelectedId(addresses[0].id || "");
            }
        } else {
            setSelectedId(null); // No address available
        }
    }, [addresses]);

    useEffect(() => {
        const selected = addresses?.find((address) => address.id === selectedId) || addresses?.[0];
        if (selected) {
            setSelectedAddress(selected)
            inputAddressRef.current = selected;
        }
    }, [addresses, selectedId]);

    const handleSaveAddress = (mode: "create" | "edit") => {
        const validAddress = ref.current?.validate();

        if (validAddress?.isValid && validAddress.address) {
            let list:UserAddress[];

            if (mode === "create") {
                list = [...addresses || [], validAddress.address]
            } else {
                list = addresses?.map(item => {
                    return item.id === selectedAddress?.id ? validAddress.address! : item
                }) || []
            }

            if (list) {
                updateAddresses(list);
            }

            closeModal();
            showToast(
                <>
                    {getTranslation("AddressList.toastMessage.addressSaved")}
                </>,
                IconNames.Checkmark
            );
        } else {
            console.error('Something went wrong')
        }
    };

    const handleCreateAddress = () => {
        inputAddressRef.current = blank;
        openModal("create");
    }

    const handleCancelEditAddress = () => {
        closeModal();        
    }

    const handleEditItemClick = () => {
        if (selectedAddress) {
            inputAddressRef.current = selectedAddress;
        }
        openModal("edit");
    }

    const handleDeleteItemClick = () => {
        if (selectedAddress) {
            handleConfirmDelete();
        }
    }

    const handleDeleteAddress = () => {
        if (selectedAddress && addresses) {
            const list = [...addresses.filter(adr => adr.id !== selectedAddress.id)]
            updateAddresses(list);
        }
    }

    const handleConfirmDelete = () => {
        showModal(
            <div>
                {getTranslation("AddressList.confirmDeleteAddress")}
            </div>,
            {
                size: ModalSize.SM,
                shouldCloseOnOverlayClick: true,
                shouldCloseOnEsc: true,
                shouldCloseOnAction: true,
                title: getTranslation("AddressList.confirmDeleteAddressTitle"),
                actionButton: {
                    label: `${getTranslation("button.delete")}`,
                    type:  ModalButtonType.DELETE,
                    onClick: handleDeleteAddress,
                },
                cancelButton: {
                    label: `${getTranslation("button.cancel")}`,
                    onClick: () => closeModal(),
                }
            }
        )
    }

    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    //Close doopdowm if clicking outside
    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const handleListClick = (addressId: string) => {
        setSelectedId(addressId);
        toggleDropdown(); //close dropdown after choosing address
    };    

    const openModal = (mode: "create" | "edit") => {
        showModal(
            <AddressEdit
                ref={ref}
                address={inputAddressRef.current!}
                allowSaveAddressOverride={false}
            />
            ,
            {
                size: ModalSize.XL,
                shouldCloseOnOverlayClick: true,
                shouldCloseOnEsc: true,
                shouldCloseOnAction: false,
                title: `${getTranslation(mode === "edit" ? "AddressList.editAddress" : "AddressList.createAddress")}`,
                actionButton: {
                    label: `${getTranslation(mode === "edit" ? "button.save" : "button.create")}`,
                    type: mode === "edit" ? ModalButtonType.SUBMIT : ModalButtonType.SAVE,
                    onClick: () => handleSaveAddress(mode),
                },
                cancelButton: {
                    label: `${getTranslation("button.cancel")}`,
                    onClick: handleCancelEditAddress,
                },
            }
        );        
    }

    return (
        <div className={S.wrapper}>
            <div className={S.addressList}>
                <div>
                    <Button
                        iconName={IconNames.Plus}
                        type="submit"
                        variant="primary"
                        iconPlacement="left"
                        className={S.addNewListBtn}
                        onClick={handleCreateAddress}
                    >
                        {getTranslation("AddressList.newAddress")}
                    </Button>
                </div>

                <div className={S.listWrapper} ref={dropdownRef}>
                    {selectedAddress && (
                        <div
                            role="button"
                            onClick={toggleDropdown}
                            className={`${S.listWrapperBtn} ${isOpen ? S.listWrapperShow : ""}`}
                        >
                            <div className={cn(S.listBoxItem)}>
                                <p>{getTranslation("AddressList.selectAddress")}</p>
                                <div className={S.selectedList}>
                                    <div className={S.listBoxItemTitle}>{selectedAddress.alias}</div>
                                    <SvgIcon iconName={IconNames.Smallarrowright} />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={`${S.dropdownContent} ${isOpen ? S.show : ""}`}>
                        <div className={S.listBox}>
                            {addresses && addresses?.length > 0 ? (
                                addresses?.map((address) => (
                                    <div
                                        key={address.id}
                                        className={cn(S.listBoxItem, {
                                            [S.listBoxItemActive]: address.id === selectedId,
                                        })}
                                        onClick={() => handleListClick(address.id || "")}
                                    >
                                        <div className={S.listBoxItemTitle}>{address.alias}</div>
                                        <span className={S.listBoxItemSubtitle}>
                                            {address.postalCode}
                                        </span>
                                        {address.id === selectedId && (
                                            <>
                                                <span className={S.activeArrowIcon}>
                                                    <SvgIcon iconName={IconNames.Smallarrowright} />
                                                </span>
                                                <span className={S.activeCheckmarkIcon}>
                                                    <SvgIcon iconName={IconNames.Checkmark} />
                                                </span>
                                            </>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <EmptyState heading={getTranslation("AddressList.noItemsAvalible")} />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <h1>{getTranslation("AddressList.pageTitle")}</h1>

                {selectedAddress ? (
                    <div className={S.selectedBox}>
                        <div className={S.selectedBoxHeader}>
                            <h3 className={S.selectedBoxTitle}>{selectedAddress.alias}</h3>
                            <div className={S.selectedBoxHeaderButtons}>
                                <Button
                                    variant="full-transparent"
                                    className={S.titleEditBtn}
                                    onClick={handleEditItemClick}
                                >
                                    <SvgIcon iconName={IconNames.Pencil} />
                                </Button>
                                <Button
                                    variant="full-transparent"
                                    className={S.titleDeleteBtn}
                                    onClick={handleDeleteItemClick}
                                >
                                    <SvgIcon iconName={IconNames.Trashcan} />
                                </Button>
                            </div>
                        </div>

                        <div className={S.selectedBoxInfo}>
                            {selectedAddress.streetName}
                        </div>
                        <div className={S.selectedBoxInfoSmall}>
                            {selectedAddress.postalCode}&nbsp; 
                            {selectedAddress.cityName}<br/>
                            {countryFromCode(selectedAddress.countryId)} ({selectedAddress.countryId})
                        </div>
                    </div>
                ): null}

            </div>
        </div>
    )
}
 
export default AddressList;